import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Cognitive/Psychosocial Development subsection adapter
 * Handles communication, cognitive assessment, and substance abuse history (5 fields)
 */
export class CognitivePsychosocialAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cognitive/Psychosocial assessment fields
    const communicationAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT
    )

    const cognitiveAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT
    )

    const historySubstanceAbuse = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE
    )?.at(0)

    const substanceAbuseNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT]: communicationAssessment,
      [SocFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT]: cognitiveAssessment,
      [SocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE]: historySubstanceAbuse,
      [SocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES]: substanceAbuseNotes,
      [SocFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT,
        value: this.getFormDataField(SocFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT,
        value: this.getFormDataField(SocFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)
      }
    ]

    if (this.formDataFieldExists(SocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE,
        value: this.getFormDataField(SocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)
      })
    }

    // Conditionally add substance abuse notes if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(SocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES,
        value: this.getFormDataField(SocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)
      })
    }

    return mutations
  }
}
